import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCalendarAnalytics, getMonthlyReport } from '../../../api/home';
import type { CalendarDay, MonthlyReportResponse } from '../../../api/types';
import { useAnonymousUser } from '../../../hooks/useAnonymousUser';
import PullToRefresh from './PullToRefresh';

interface CalendarScreenProps {
  onBack: () => void;
}

type Emotion = 'joy' | 'sadness' | 'fear' | 'anger';

const EMOTION_COLOR: Record<Emotion, string> = {
  joy: '#3ECFA0',
  sadness: '#4A90E2',
  fear: '#6B4FA8',
  anger: '#FF6B6B',
};

const EMOTION_BG: Record<Emotion, string> = {
  joy: 'rgba(62,207,160,0.15)',
  sadness: 'rgba(74,144,226,0.15)',
  fear: 'rgba(107,79,168,0.15)',
  anger: 'rgba(255,107,107,0.15)',
};

const EMOTION_LABEL: Record<Emotion, string> = {
  joy: 'Радость',
  sadness: 'Печаль',
  fear: 'Страх',
  anger: 'Гнев',
};

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const MONTH_NAMES_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

const DAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const EMOTIONS: Emotion[] = ['joy', 'sadness', 'fear', 'anger'];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number) {
  const d = new Date(year, month - 1, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
}

function emotionIcon(emotion: Emotion) {
  if (emotion === 'joy') return 'ri-sun-line';
  if (emotion === 'sadness') return 'ri-drop-line';
  if (emotion === 'fear') return 'ri-moon-line';
  return 'ri-fire-line';
}

function isEmotion(value: string | null | undefined): value is Emotion {
  return value === 'joy' || value === 'sadness' || value === 'fear' || value === 'anger';
}

function buildInsight(days: CalendarDay[], fallback: string | null): { emotion: Emotion; text: string } | null {
  const counts: Record<Emotion, number> = { joy: 0, sadness: 0, fear: 0, anger: 0 };
  days.forEach((day) => {
    if (isEmotion(day.dominant_emotion)) counts[day.dominant_emotion] += 1;
  });
  const top = (Object.entries(counts) as [Emotion, number][]).sort((a, b) => b[1] - a[1])[0];
  if (!top || top[1] === 0) return null;
  const text = fallback || {
    joy: 'В этом месяце радость чаще всего преобладала в ваших воспоминаниях.',
    sadness: 'В этом месяце вы чаще всего переживали грусть и задумчивость.',
    fear: 'В этом месяце тревога нередко окрашивала ваши воспоминания.',
    anger: 'В этом месяце случалось немало напряжённых моментов.',
  }[top[0]];
  return { emotion: top[0], text };
}

export default function CalendarScreen({ onBack }: CalendarScreenProps) {
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [days, setDays] = useState<CalendarDay[]>([]);
  const [insightText, setInsightText] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [monthlyReport, setMonthlyReport] = useState<MonthlyReportResponse | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { userId, loading: userLoading, error: userError } = useAnonymousUser();

  const refreshCalendar = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setLoadError(null);

    try {
      const data = await getCalendarAnalytics({ userId, year, month });
      setDays(data.days);
      setInsightText(data.insight);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Не удалось загрузить календарь');
    } finally {
      setLoading(false);
    }
  }, [month, userId, year]);

  const loadMonthlyReport = useCallback(async () => {
    if (!userId) return;

    setReportLoading(true);
    setReportError(null);

    try {
      const data = await getMonthlyReport({ userId, year, month });
      setMonthlyReport(data);
    } catch (error) {
      setReportError(error instanceof Error ? error.message : 'Не удалось собрать историю месяца');
    } finally {
      setReportLoading(false);
    }
  }, [month, userId, year]);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    getCalendarAnalytics({ userId, year, month })
      .then((data) => {
        if (cancelled) return;
        setDays(data.days);
        setInsightText(data.insight);
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadError(error instanceof Error ? error.message : 'Не удалось загрузить календарь');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, year, month]);

  useEffect(() => {
    setMonthlyReport(null);
    setReportError(null);
    setReportOpen(false);
  }, [year, month]);

  const dayMap = useMemo(() => new Map(days.map((day) => [Number(day.date.slice(8, 10)), day])), [days]);
  const daysInMonth = getDaysInMonth(year, month);
  const firstDow = getFirstDayOfWeek(year, month);
  const insight = buildInsight(days, insightText);
  const effectiveError = userError || loadError;

  const goBack = () => {
    if (month === 1) { setMonth(12); setYear((value) => value - 1); }
    else setMonth((value) => value - 1);
    setSelectedDay(null);
  };
  const goForward = () => {
    if (month === 12) { setMonth(1); setYear((value) => value + 1); }
    else setMonth((value) => value + 1);
    setSelectedDay(null);
  };

  const selected = selectedDay ? dayMap.get(selectedDay) ?? null : null;

  const openMonthlyReport = () => {
    setReportOpen(true);
    setSelectedDay(null);
    if (!monthlyReport || monthlyReport.year !== year || monthlyReport.month !== month) {
      void loadMonthlyReport();
    }
  };

  if (reportOpen) {
    return (
      <MonthlyReportScreen
        year={year}
        month={month}
        report={monthlyReport}
        loading={reportLoading}
        error={userError || reportError}
        onBack={() => setReportOpen(false)}
        onRefresh={loadMonthlyReport}
      />
    );
  }

  return (
    <PullToRefresh
      onRefresh={refreshCalendar}
      disabled={userLoading || loading || !userId}
    >
      <div
        style={{
          minHeight: '100%',
          background: 'linear-gradient(160deg, #1A1B2E 0%, #2D1B3D 100%)',
          paddingBottom: 'calc(32px + env(safe-area-inset-bottom))',
        }}
      >
        <div
          style={{
            position: 'sticky', top: 0, zIndex: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 20px', height: '56px',
            background: 'rgba(26,27,46,0.95)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <div style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ri-arrow-left-s-line" style={{ fontSize: '22px', color: '#3ECFA0' }} />
            </div>
            <span style={{ fontSize: '15px', fontWeight: 500, color: '#3ECFA0' }}>Главная</span>
          </button>
          <span style={{ fontSize: '18px', fontWeight: 600, color: '#F5F5F7' }}>Календарь</span>
          <div style={{ width: '80px' }} />
        </div>

        <div style={{ padding: '20px 16px 0', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p style={{ fontSize: '13px', color: '#6B7A94', lineHeight: 1.6, textAlign: 'center', padding: '0 8px' }}>
          Смотрите, как менялись ваши воспоминания и эмоции день за днём.
        </p>

        {effectiveError && (
          <div style={{ background: 'rgba(255,107,107,0.12)', border: '1px solid rgba(255,107,107,0.25)', borderRadius: '16px', padding: '14px 16px', color: '#FFD1D1', fontSize: '13px', lineHeight: 1.5 }}>
            Не удалось загрузить календарь: {effectiveError}
          </div>
        )}

        <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '24px', padding: '20px 16px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <button onClick={goBack} style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <i className="ri-arrow-left-s-line" style={{ fontSize: '18px', color: '#A8B2C8' }} />
            </button>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '17px', fontWeight: 700, color: '#F5F5F7', letterSpacing: '0.2px' }}>{MONTH_NAMES[month - 1]}</p>
              <p style={{ fontSize: '12px', color: '#6B7A94', marginTop: '1px' }}>{year}</p>
            </div>
            <button onClick={goForward} style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <i className="ri-arrow-right-s-line" style={{ fontSize: '18px', color: '#A8B2C8' }} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '8px' }}>
            {DAY_NAMES.map((day) => <div key={day} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 600, color: '#6B7A94', padding: '2px 0' }}>{day}</div>)}
          </div>

          {loading || userLoading ? (
            <div style={{ height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7A94', fontSize: '13px' }}>
              Загружаем календарь...
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px' }}>
              {Array.from({ length: firstDow }).map((_, index) => <div key={`empty-${index}`} />)}
              {Array.from({ length: daysInMonth }, (_, index) => {
                const day = index + 1;
                const data = dayMap.get(day);
                const emotion = isEmotion(data?.dominant_emotion) ? data.dominant_emotion : null;
                const color = emotion ? EMOTION_COLOR[emotion] : null;
                const isSelected = selectedDay === day;
                const isToday = year === today.getFullYear() && month === today.getMonth() + 1 && day === today.getDate();
                return (
                  <button
                    key={day}
                    onClick={() => setSelectedDay(isSelected ? null : day)}
                    style={{
                      aspectRatio: '1', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer',
                      background: isSelected ? (color ?? 'rgba(255,255,255,0.15)') : (color ? `${color}26` : 'transparent'),
                      border: isSelected ? `2px solid ${color ?? '#fff'}` : color ? `1.5px solid ${color}60` : isToday ? '2px solid #3ECFA0' : '1.5px solid rgba(255,255,255,0.1)',
                      boxShadow: isSelected && color ? `0 0 12px ${color}60` : 'none',
                      fontSize: '12px', fontWeight: isToday || isSelected ? 700 : 400,
                      color: isSelected ? '#fff' : color ?? '#A8B2C8',
                      transition: 'all 0.15s ease', padding: 0,
                    }}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <Legend />

        {insight && <InsightCard insight={insight} />}

        <MonthlyReportCard
          year={year}
          month={month}
          days={days}
          onOpen={openMonthlyReport}
        />

        <div>
          <p style={{ fontSize: '15px', fontWeight: 600, color: '#F5F5F7', marginBottom: '12px' }}>Эмоции за месяц</p>
          <EmotionStats days={days} />
        </div>
        </div>

        {selectedDay !== null && (
          <DaySheet day={selectedDay} month={month} year={year} data={selected} onClose={() => setSelectedDay(null)} />
        )}
      </div>
    </PullToRefresh>
  );
}

function Legend() {
  return (
    <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '16px', padding: '14px 16px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
      {([
        { color: '#3ECFA0', label: 'Радость' },
        { color: '#4A90E2', label: 'Печаль' },
        { color: '#6B4FA8', label: 'Страх' },
        { color: '#FF6B6B', label: 'Гнев' },
        { color: 'rgba(255,255,255,0.15)', label: 'Нет воспоминаний', border: 'rgba(255,255,255,0.2)' },
      ] as { color: string; label: string; border?: string }[]).map((item) => (
        <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: item.color, border: item.border ? `1px solid ${item.border}` : 'none', flexShrink: 0 }} />
          <span style={{ fontSize: '11px', color: '#A8B2C8', whiteSpace: 'nowrap' }}>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function InsightCard({ insight }: { insight: { emotion: Emotion; text: string } }) {
  return (
    <div style={{ background: EMOTION_BG[insight.emotion], border: `1px solid ${EMOTION_COLOR[insight.emotion]}30`, borderRadius: '20px', padding: '16px 18px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: `${EMOTION_COLOR[insight.emotion]}25`, border: `1px solid ${EMOTION_COLOR[insight.emotion]}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <i className={emotionIcon(insight.emotion)} style={{ fontSize: '16px', color: EMOTION_COLOR[insight.emotion] }} />
      </div>
      <div>
        <p style={{ fontSize: '12px', fontWeight: 600, color: EMOTION_COLOR[insight.emotion], marginBottom: '4px' }}>Наблюдение месяца</p>
        <p style={{ fontSize: '13px', color: '#C8D0E0', lineHeight: 1.55 }}>{insight.text}</p>
      </div>
    </div>
  );
}

function MonthlyReportCard({
  year,
  month,
  days,
  onOpen,
}: {
  year: number;
  month: number;
  days: CalendarDay[];
  onOpen: () => void;
}) {
  const memoriesCount = days.reduce((sum, day) => sum + day.memories_count, 0);
  const activeDays = days.filter((day) => day.memories_count > 0).length;

  return (
    <button
      onClick={onOpen}
      style={{
        width: '100%',
        border: '1px solid rgba(62,207,160,0.24)',
        background: 'linear-gradient(145deg, rgba(62,207,160,0.12), rgba(74,144,226,0.09))',
        borderRadius: '22px',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '13px',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <div style={{ width: '44px', height: '44px', borderRadius: '16px', background: 'rgba(62,207,160,0.17)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <i className="ri-book-open-line" style={{ fontSize: '20px', color: '#3ECFA0' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '15px', fontWeight: 700, color: '#F5F5F7', marginBottom: '4px' }}>
          Краткая история месяца
        </p>
        <p style={{ fontSize: '12px', color: '#A8B2C8', lineHeight: 1.5 }}>
          {MONTH_NAMES[month - 1]} {year}: {memoriesCount} {memoriesCount === 1 ? 'воспоминание' : memoriesCount < 5 ? 'воспоминания' : 'воспоминаний'}, {activeDays} {activeDays === 1 ? 'день' : activeDays < 5 ? 'дня' : 'дней'}
        </p>
      </div>
      <i className="ri-arrow-right-s-line" style={{ fontSize: '22px', color: '#3ECFA0', flexShrink: 0 }} />
    </button>
  );
}

function MonthlyReportScreen({
  year,
  month,
  report,
  loading,
  error,
  onBack,
  onRefresh,
}: {
  year: number;
  month: number;
  report: MonthlyReportResponse | null;
  loading: boolean;
  error: string | null;
  onBack: () => void;
  onRefresh: () => Promise<void>;
}) {
  return (
    <PullToRefresh onRefresh={onRefresh} disabled={loading}>
      <div
        style={{
          minHeight: '100%',
          background: 'linear-gradient(160deg, #1A1B2E 0%, #2D1B3D 100%)',
          paddingBottom: 'calc(32px + env(safe-area-inset-bottom))',
        }}
      >
        <div
          style={{
            position: 'sticky', top: 0, zIndex: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 20px', height: '56px',
            background: 'rgba(26,27,46,0.95)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <div style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ri-arrow-left-s-line" style={{ fontSize: '22px', color: '#3ECFA0' }} />
            </div>
            <span style={{ fontSize: '15px', fontWeight: 500, color: '#3ECFA0' }}>Календарь</span>
          </button>
          <span style={{ fontSize: '17px', fontWeight: 600, color: '#F5F5F7' }}>История месяца</span>
          <div style={{ width: '92px' }} />
        </div>

        <div style={{ padding: '20px 16px 0', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ borderRadius: '24px', padding: '18px', background: 'linear-gradient(145deg, rgba(62,207,160,0.14), rgba(74,144,226,0.1))', border: '1px solid rgba(62,207,160,0.22)' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: '#3ECFA0', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {MONTH_NAMES[month - 1]} {year}
            </p>
            <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#F5F5F7', lineHeight: 1.22, marginBottom: '10px' }}>
              Краткая история месяца
            </h2>
            <p style={{ fontSize: '13px', color: '#A8B2C8', lineHeight: 1.6 }}>
              {report
                ? `${report.memories_count} ${report.memories_count === 1 ? 'воспоминание' : report.memories_count < 5 ? 'воспоминания' : 'воспоминаний'} за ${report.active_days_count} ${report.active_days_count === 1 ? 'день' : report.active_days_count < 5 ? 'дня' : 'дней'}`
                : 'Собираем воспоминания, эмоции и события месяца.'}
            </p>
          </div>

          {error && (
            <div style={{ background: 'rgba(255,107,107,0.12)', border: '1px solid rgba(255,107,107,0.25)', borderRadius: '16px', padding: '14px 16px', color: '#FFD1D1', fontSize: '13px', lineHeight: 1.5 }}>
              {error}
            </div>
          )}

          {loading && !report ? (
            <div style={{ height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7A94', fontSize: '13px' }}>
              Собираем историю месяца...
            </div>
          ) : report ? (
            <>
              <section style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '22px', padding: '18px 16px' }}>
                <p style={{ fontSize: '15px', fontWeight: 700, color: '#F5F5F7', marginBottom: '12px' }}>История</p>
                <p style={{ fontSize: '14px', color: '#C8D0E0', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                  {report.story_text}
                </p>
              </section>

              <section>
                <p style={{ fontSize: '15px', fontWeight: 700, color: '#F5F5F7', marginBottom: '12px' }}>Эмоциональная оценка</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
                  {EMOTIONS.map((emotion) => (
                    <EmotionReportTile
                      key={emotion}
                      emotion={emotion}
                      average={report.averages[emotion] ?? 0}
                      maximum={report.maximums[emotion] ?? 0}
                    />
                  ))}
                </div>
              </section>

              <section>
                <p style={{ fontSize: '15px', fontWeight: 700, color: '#F5F5F7', marginBottom: '12px' }}>Топ эмоциональных событий</p>
                {report.top_memories.length ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {report.top_memories.map((memory, index) => {
                      const emotion = isEmotion(memory.dominant_emotion) ? memory.dominant_emotion : 'joy';
                      return (
                        <div key={memory.id} style={{ background: EMOTION_BG[emotion], border: `1px solid ${EMOTION_COLOR[emotion]}30`, borderRadius: '18px', padding: '14px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '7px' }}>
                            <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: `${EMOTION_COLOR[emotion]}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: EMOTION_COLOR[emotion], fontSize: '13px', fontWeight: 800 }}>
                              {index + 1}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <p style={{ fontSize: '14px', fontWeight: 700, color: '#F5F5F7', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{memory.title}</p>
                              <p style={{ fontSize: '12px', color: '#A8B2C8', marginTop: '2px' }}>{formatDate(memory.event_date)} · {EMOTION_LABEL[emotion]} {memory.score}/10</p>
                            </div>
                          </div>
                          <p style={{ fontSize: '13px', color: '#A8B2C8', lineHeight: 1.55, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {memory.text || 'Текст воспоминания пока не заполнен.'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '18px', padding: '18px', color: '#A8B2C8', fontSize: '13px', lineHeight: 1.55 }}>
                    В этом месяце пока нет событий для рейтинга.
                  </div>
                )}
              </section>
            </>
          ) : null}
        </div>
      </div>
    </PullToRefresh>
  );
}

function EmotionReportTile({
  emotion,
  average,
  maximum,
}: {
  emotion: Emotion;
  average: number;
  maximum: number;
}) {
  return (
    <div style={{ background: EMOTION_BG[emotion], border: `1px solid ${EMOTION_COLOR[emotion]}30`, borderRadius: '18px', padding: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: `${EMOTION_COLOR[emotion]}25`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className={emotionIcon(emotion)} style={{ fontSize: '14px', color: EMOTION_COLOR[emotion] }} />
        </div>
        <span style={{ fontSize: '13px', fontWeight: 700, color: '#F5F5F7' }}>{EMOTION_LABEL[emotion]}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <div>
          <p style={{ fontSize: '11px', color: '#6B7A94', marginBottom: '2px' }}>Среднее</p>
          <p style={{ fontSize: '20px', fontWeight: 800, color: EMOTION_COLOR[emotion] }}>{average}</p>
        </div>
        <div>
          <p style={{ fontSize: '11px', color: '#6B7A94', marginBottom: '2px' }}>Максимум</p>
          <p style={{ fontSize: '20px', fontWeight: 800, color: '#F5F5F7' }}>{maximum}</p>
        </div>
      </div>
    </div>
  );
}

function EmotionStats({ days }: { days: CalendarDay[] }) {
  const counts: Record<Emotion, number> = { joy: 0, sadness: 0, fear: 0, anger: 0 };
  days.forEach((day) => {
    if (isEmotion(day.dominant_emotion)) counts[day.dominant_emotion] += 1;
  });
  const total = Object.values(counts).reduce((sum, value) => sum + value, 0) || 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {EMOTIONS.map((emotion) => {
        const count = counts[emotion];
        const pct = Math.round((count / total) * 100);
        return (
          <div key={emotion}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <span style={{ fontSize: '13px', fontWeight: 500, color: '#C8D0E0' }}>{EMOTION_LABEL[emotion]}</span>
              <span style={{ fontSize: '12px', color: '#6B7A94' }}>{count} {count === 1 ? 'день' : count < 5 ? 'дня' : 'дней'}</span>
            </div>
            <div style={{ height: '6px', borderRadius: '3px', background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, borderRadius: '3px', background: EMOTION_COLOR[emotion], transition: 'width 0.4s ease' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface DaySheetProps {
  day: number;
  month: number;
  year: number;
  data: CalendarDay | null;
  onClose: () => void;
}

function DaySheet({ day, month, year, data, onClose }: DaySheetProps) {
  const emotion = isEmotion(data?.dominant_emotion) ? data.dominant_emotion : null;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', zIndex: 100 }} />
      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 101, background: 'linear-gradient(180deg, #232438 0%, #1E1F35 100%)', borderTopLeftRadius: '28px', borderTopRightRadius: '28px', border: '1px solid rgba(255,255,255,0.09)', borderBottom: 'none', padding: '0 0 calc(40px + env(safe-area-inset-bottom))', boxShadow: '0 -20px 60px rgba(0,0,0,0.5)', maxHeight: '70vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.15)' }} />
        </div>
        <div style={{ padding: '12px 24px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div>
              <p style={{ fontSize: '22px', fontWeight: 700, color: '#F5F5F7' }}>{day} {MONTH_NAMES_GEN[month - 1]} {year}</p>
              {emotion && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: EMOTION_COLOR[emotion] }} />
                  <span style={{ fontSize: '13px', color: EMOTION_COLOR[emotion], fontWeight: 500 }}>{EMOTION_LABEL[emotion]} — доминирующая эмоция</span>
                </div>
              )}
            </div>
            <button onClick={onClose} style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ri-close-line" style={{ fontSize: '16px', color: '#A8B2C8' }} />
            </button>
          </div>

          {!data || data.memories.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 16px', gap: '12px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ri-moon-line" style={{ fontSize: '24px', color: '#6B7A94' }} />
              </div>
              <p style={{ fontSize: '15px', fontWeight: 500, color: '#C8D0E0', textAlign: 'center' }}>В этот день воспоминаний не сохранено.</p>
              <p style={{ fontSize: '13px', color: '#6B7A94', textAlign: 'center', lineHeight: 1.55 }}>Каждый день — возможность сохранить что-то важное.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {data.memories.map((memory) => {
                const memoryEmotion = isEmotion(memory.dominant_emotion) ? memory.dominant_emotion : emotion ?? 'joy';
                return (
                  <div key={memory.id} style={{ background: EMOTION_BG[memoryEmotion], border: `1px solid ${EMOTION_COLOR[memoryEmotion]}30`, borderRadius: '18px', padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '7px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: `${EMOTION_COLOR[memoryEmotion]}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className={emotionIcon(memoryEmotion)} style={{ fontSize: '13px', color: EMOTION_COLOR[memoryEmotion] }} />
                      </div>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: '#F5F5F7' }}>{memory.title}</span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#A8B2C8', lineHeight: 1.55, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {memory.text || 'Текст воспоминания пока не заполнен.'}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
